/* ==========================================================================
   VELÁ, behaviour for the custom sections.
   Defensive by design: every component no-ops if its markup is absent, so a
   merchant can delete or reorder any section without breaking another.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- reveal */

  function initReveal(root) {
    var nodes = (root || document).querySelectorAll('.vela-reveal:not([data-revealed])');
    if (!nodes.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) {
        n.setAttribute('data-revealed', '');
        n.classList.add('is-in');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    nodes.forEach(function (n) {
      n.setAttribute('data-revealed', '');
      io.observe(n);
    });
  }

  /* ------------------------------------------------------ scroll helper */

  // Native smooth scrolling is the nice path, but it is unreliable next to
  // scroll snapping and absent in some environments, so always guarantee the
  // rail actually lands on the target.
  function scrollRailTo(el, target, onSettled) {
    var max = el.scrollWidth - el.clientWidth;
    var to = Math.max(0, Math.min(Math.round(target), max));

    if (!reduceMotion && typeof el.scrollTo === 'function') {
      try {
        el.scrollTo({ left: to, behavior: 'smooth' });
      } catch (e) {
        el.scrollLeft = to;
      }
    } else {
      el.scrollLeft = to;
    }

    clearTimeout(el._velaSettle);
    el._velaSettle = setTimeout(function () {
      if (Math.abs(el.scrollLeft - to) > 2) el.scrollLeft = to;
      if (typeof onSettled === 'function') onSettled();
    }, 420);
  }

  /* --------------------------------------------------------- init guard */

  function claim(root, key) {
    if (!root) return false;
    var flag = '_velaInit_' + key;
    if (root[flag]) return false;
    root[flag] = true;
    return true;
  }

  /* ------------------------------------------------------------ money util */

  function moneyFormat() {
    return (window.velaMoneyFormat || '${{amount}}');
  }

  function formatMoney(cents) {
    var fmt = moneyFormat();
    var value = (cents / 100);
    // Whole amounts drop their ".00" (Rs. 1,499 rather than Rs. 1,499.00).
    if (Math.round(cents) % 100 === 0) {
      fmt = fmt.replace(/\{\{\s*amount\s*\}\}/g, '{{amount_no_decimals}}')
               .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, '{{amount_no_decimals_with_comma_separator}}');
    }

    function withCommas(num, decimals) {
      var parts = num.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }

    function withDots(num, decimals) {
      var parts = num.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return parts.join(decimals ? ',' : '');
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_m, name) {
      switch (name) {
        case 'amount':
          return withCommas(value, 2);
        case 'amount_no_decimals':
          return withCommas(value, 0);
        case 'amount_with_comma_separator':
          return withDots(value, 2);
        case 'amount_no_decimals_with_comma_separator':
          return withDots(value, 0);
        case 'amount_with_space_separator':
          return withCommas(value, 2).replace(/,/g, ' ').replace('.', ',');
        case 'amount_no_decimals_with_space_separator':
          return withCommas(value, 0).replace(/,/g, ' ');
        case 'amount_with_apostrophe_separator':
          return withCommas(value, 2).replace(/,/g, "'");
        default:
          return withCommas(value, 2);
      }
    });
  }

  /* ------------------------------------------------------------ cart bridge */

  function cartHost() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function sectionIdsForCart() {
    var host = cartHost();
    if (host && typeof host.getSectionsToRender === 'function') {
      return host
        .getSectionsToRender()
        .map(function (s) { return s.id; })
        .join(',');
    }
    return '';
  }

  function addToCart(items, onDone, onError) {
    var body = { items: items };
    var ids = sectionIdsForCart();
    if (ids) {
      body.sections = ids;
      body.sections_url = window.location.pathname;
    }

    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

    fetch(addUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
      .then(function (res) {
        if (!res.ok || res.json.status) {
          onError(res.json.description || res.json.message || 'Sorry, that could not be added to your cart.');
          return;
        }
        onDone(res.json);
      })
      .catch(function () {
        onError('Something went wrong. Please try again.');
      });
  }

   function afterAdd(response, trigger) {
    var host = cartHost();

    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'vela-bundle',
        productVariantId: response.id,
        cartData: response
      });
    }

    if (host && response.sections) {
      // The drawer keeps an is-empty class from when the cart was empty, and
      // its CSS hides the header and shows the empty state. renderContents
      // swaps the inner HTML but never clears this, so the freshly added items
      // end up in the DOM yet invisible until the next page load.
      if (host.classList) host.classList.remove('is-empty');

      var innerDrawer = host.querySelector ? host.querySelector('.drawer__inner') : null;
      if (innerDrawer) innerDrawer.classList.remove('is-empty');

      if (typeof host.setActiveElement === 'function' && trigger) host.setActiveElement(trigger);
      host.renderContents(response);
      return;
    }

    window.location = (window.routes && window.routes.cart_url) || '/cart';
  }

  /* ----------------------------------------------------------- bundle block */

  function VelaBundle(root) {
    if (!claim(root, 'bundle')) return;

    var form = root.querySelector('[data-vela-bundle-form]');
    var submit = root.querySelector('[data-vela-submit]');
    var errBox = root.querySelector('[data-vela-error]');
    var totalNow = root.querySelector('[data-vela-total]');
    var totalWas = root.querySelector('[data-vela-was]');
    var saveEl = root.querySelector('[data-vela-save]');
    var tiers = Array.prototype.slice.call(root.querySelectorAll('[data-vela-tier]'));
    var redirect = root.getAttribute('data-redirect') === 'true';
    if (!tiers.length || !submit) return;

    function checkedAddons() {
      return Array.prototype.slice.call(root.querySelectorAll('[data-vela-addon]')).filter(function (row) {
        var box = row.querySelector('[data-vela-addon-check]');
        return box && box.checked && !box.disabled;
      });
    }

    function activeTier() {
      for (var i = 0; i < tiers.length; i++) {
        var input = tiers[i].querySelector('.vela-tier__input');
        if (input && input.checked) return tiers[i];
      }
      return tiers[0];
    }

    function showError(msg) {
      if (!errBox) return;
      errBox.textContent = msg;
      errBox.classList.add('is-on');
    }

    function clearError() {
      if (!errBox) return;
      errBox.textContent = '';
      errBox.classList.remove('is-on');
    }

    function syncTotals() {
      var tier = activeTier();
      if (!tier) return;

      var price = parseInt(tier.getAttribute('data-price'), 10) || 0;
      var compare = parseInt(tier.getAttribute('data-compare'), 10) || 0;
      if (compare < price) compare = price;

      checkedAddons().forEach(function (row) {
        var ap = parseInt(row.getAttribute('data-price'), 10) || 0;
        var ac = parseInt(row.getAttribute('data-compare'), 10) || 0;
        price += ap;
        compare += Math.max(ac, ap);
      });

      if (totalNow) totalNow.textContent = formatMoney(price);

      if (totalWas) {
        if (compare > price) {
          totalWas.textContent = formatMoney(compare);
          totalWas.hidden = false;
        } else {
          totalWas.hidden = true;
        }
      }

      if (saveEl) {
        if (compare > price) {
          saveEl.textContent = saveEl.getAttribute('data-template').replace('[amount]', formatMoney(compare - price));
          saveEl.hidden = false;
        } else {
          saveEl.hidden = true;
        }
      }
    }

    function syncActive() {
      tiers.forEach(function (tier) {
        var input = tier.querySelector('.vela-tier__input');
        var on = !!(input && input.checked);
        tier.classList.toggle('is-active', on);

        // Only the chosen tier's colour choices take part in the form.
        tier.querySelectorAll('.vela-swatch input').forEach(function (radio) {
          radio.disabled = !on;
        });
      });
      syncTotals();
      syncPickedNames();
      syncColour();
      clearError();
    }

    // Sold-out colours can be picked to preview them, but not bought.
    function soldOutPicked() {
      var tier = activeTier();
      if (!tier) return false;
      return Array.prototype.some.call(tier.querySelectorAll('.vela-swatch input:checked'), function (input) {
        return input.getAttribute('data-available') === 'false';
      });
    }

    function syncColour(input) {
      var tier = activeTier();
      if (!input && tier) input = tier.querySelector('.vela-swatch input:checked');
      var out = soldOutPicked();

      if (!submit.hasAttribute('data-label')) submit.setAttribute('data-label', submit.textContent.trim());
      submit.textContent = out ? (root.getAttribute('data-soldout-label') || 'Out of stock') : submit.getAttribute('data-label');
      if (out) submit.setAttribute('aria-disabled', 'true');
      else submit.removeAttribute('aria-disabled');
      root.classList.toggle('is-soldout', out);

      var stock = root.querySelector('.vela-stock');
      if (stock) stock.hidden = out;

      if (!input) return;
      var scope = root.closest('.shopify-section') || document;
      var gallery = scope.querySelector('[data-vela-gallery]');
      if (gallery && gallery.velaSetColour) {
        gallery.velaSetColour(input.getAttribute('data-colour'), input.getAttribute('data-available') !== 'false');
      }
    }

    function syncPickedNames() {
      root.querySelectorAll('.vela-pick').forEach(function (pick) {
        var out = pick.querySelector('[data-vela-picked-name]');
        if (!out) return;
        var checked = pick.querySelector('.vela-swatch input:checked');
        var label = checked ? checked.parentNode.querySelector('.visually-hidden') : null;
        var sold = checked && checked.getAttribute('data-available') === 'false';
        out.textContent = label ? ': ' + label.textContent.trim() + (sold ? ' (' + (root.getAttribute('data-soldout-label') || 'Out of stock') + ')' : '') : '';
      });
    }

    root.addEventListener('change', function (event) {
      if (event.target.classList.contains('vela-tier__input')) syncActive();
      if (event.target.hasAttribute('data-vela-addon-check')) syncTotals();
      if (event.target.type === 'radio' && event.target.closest('.vela-swatch')) {
        syncPickedNames();
        syncColour(event.target);
        clearError();
      }
    });

    // Clicking anywhere in a tier row selects it (the radio is visually hidden).
    tiers.forEach(function (tier) {
      tier.addEventListener('click', function (event) {
        if (event.target.closest('.vela-swatch')) return;
        var input = tier.querySelector('.vela-tier__input');
        if (input && !input.checked) {
          input.checked = true;
          syncActive();
        }
      });
    });

    function submitBundle(event) {
      if (event) event.preventDefault();
      clearError();

      var tier = activeTier();
      if (!tier) return;

      if (soldOutPicked()) {
        showError(root.getAttribute('data-msg-soldout') || 'This colour is out of stock.');
        return;
      }

      var qty = parseInt(tier.getAttribute('data-qty'), 10) || 1;
      var chosen = [];

      for (var n = 1; n <= qty; n++) {
        var picked = tier.querySelector('.vela-swatch input[data-slot="' + n + '"]:checked');
        if (!picked) {
          showError(root.getAttribute('data-msg-pick') || 'Please choose a colour for every bag.');
          return;
        }
        chosen.push(picked.value);
      }

      // Merge repeats of the same colour into one line item.
      var order = [];
      var counts = {};
      chosen.forEach(function (id) {
        if (!counts[id]) { counts[id] = 0; order.push(id); }
        counts[id] += 1;
      });

      var items = order.map(function (id) {
        return { id: parseInt(id, 10), quantity: counts[id] };
      });

      checkedAddons().forEach(function (row) {
        var pick = row.querySelector('[data-vela-addon-variant]');
        var vid = pick ? parseInt(pick.value, 10) : 0;
        if (vid) items.push({ id: vid, quantity: 1 });
      });

      var code = tier.getAttribute('data-discount') || '';

      root.setAttribute('data-loading', 'true');
      submit.setAttribute('aria-disabled', 'true');

      addToCart(
        items,
        function (response) {
          root.removeAttribute('data-loading');
          submit.removeAttribute('aria-disabled');

          // A tier may carry its own discount code instead of an automatic discount.
          if (code) {
            window.location = '/discount/' + encodeURIComponent(code) + '?redirect=' +
              encodeURIComponent((window.routes && window.routes.cart_url) || '/cart');
            return;
          }

          if (redirect) {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }

          afterAdd(response, submit);
        },
        function (msg) {
          root.removeAttribute('data-loading');
          submit.removeAttribute('aria-disabled');
          showError(msg);
        }
      );
    }

    if (form) form.addEventListener('submit', submitBundle);
    submit.addEventListener('click', submitBundle);

    syncActive();
  }

  /* ------------------------------------------------------------------ reels */

  function VelaReels(root) {
    if (!claim(root, 'reels')) return;

    var reels = Array.prototype.slice.call(root.querySelectorAll('[data-vela-reel]'));

    reels.forEach(function (reel) {
      var video = reel.querySelector('video');
      var playBtn = reel.querySelector('[data-vela-play]');
      var soundBtn = reel.querySelector('[data-vela-sound]');
      if (!video) return;

      function markPlaying(on) {
        reel.classList.toggle('is-playing', on);
        if (playBtn) {
          playBtn.setAttribute('aria-label', on ? (playBtn.getAttribute('data-pause-label') || 'Pause')
                                                : (playBtn.getAttribute('data-play-label') || 'Play'));
        }
      }

      function toggle() {
        if (video.paused) {
          var p = video.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          video.pause();
        }
      }

      if (playBtn) playBtn.addEventListener('click', toggle);
      video.addEventListener('play', function () { markPlaying(true); });
      video.addEventListener('pause', function () { markPlaying(false); });
      video.addEventListener('ended', function () { markPlaying(false); });

      if (soundBtn) {
        soundBtn.addEventListener('click', function (event) {
          event.stopPropagation();
          video.muted = !video.muted;
          soundBtn.classList.toggle('is-muted', video.muted);
          soundBtn.setAttribute('aria-pressed', String(!video.muted));
          var on = soundBtn.querySelector('[data-icon="on"]');
          var off = soundBtn.querySelector('[data-icon="off"]');
          if (on) on.hidden = video.muted;
          if (off) off.hidden = !video.muted;
        });
      }

      // Autoplay muted while on screen; pause once it scrolls away.
      if (reel.getAttribute('data-autoplay') === 'true' && !reduceMotion && 'IntersectionObserver' in window) {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                var p = video.play();
                if (p && p.catch) p.catch(function () {});
              } else if (!video.paused) {
                video.pause();
              }
            });
          },
          { threshold: 0.55 }
        );
        io.observe(reel);
      }
    });
  }

  /* ---------------------------------------------------------------- gallery */

  function VelaGallery(root) {
    if (!claim(root, 'gallery')) return;

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-vela-slide]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-vela-thumb]'));
    var prev = root.querySelector('[data-vela-prev]');
    var next = root.querySelector('[data-vela-next]');
    var oos = root.querySelector('[data-vela-oos]');
    if (slides.length < 1) return;

    // `order` lists the slide indexes currently in the gallery, in display order.
    var order = slides.map(function (_s, i) { return i; });
    var pos = 0;

    function go(p) {
      pos = (p + order.length) % order.length;
      var index = order[pos];

      slides.forEach(function (slide, n) {
        slide.classList.toggle('is-on', n === index);
        var media = slide.querySelector('video');
        if (media && n !== index && !media.paused) media.pause();
      });

      thumbs.forEach(function (thumb, n) {
        thumb.classList.toggle('is-on', n === index);
        thumb.setAttribute('aria-selected', String(n === index));
      });

      // Scroll only the thumbnail strip, never the page.
      var active = thumbs[index];
      var strip = active && active.parentNode;
      if (active && strip) {
        var a = active.getBoundingClientRect();
        var b = strip.getBoundingClientRect();
        var left = a.left - b.left + strip.scrollLeft;
        var top = a.top - b.top + strip.scrollTop;
        if (left < strip.scrollLeft || left + active.offsetWidth > strip.scrollLeft + strip.clientWidth) {
          strip.scrollLeft = left - (strip.clientWidth - active.offsetWidth) / 2;
        }
        if (top < strip.scrollTop || top + active.offsetHeight > strip.scrollTop + strip.clientHeight) {
          strip.scrollTop = top - (strip.clientHeight - active.offsetHeight) / 2;
        }
      }
    }

    thumbs.forEach(function (thumb, n) {
      thumb.addEventListener('click', function () {
        var p = order.indexOf(n);
        if (p > -1) go(p);
      });
    });

    if (prev) prev.addEventListener('click', function () { go(pos - 1); });
    if (next) next.addEventListener('click', function () { go(pos + 1); });

    // Show the chosen colour's own images first, then the shared ones.
    // A colour with no images of its own falls back to the whole gallery.
    root.velaSetColour = function (colour, available) {
      if (oos) oos.hidden = available !== false;
      if (root.getAttribute('data-filter') !== 'true') return;

      var c = String(colour || '').toLowerCase().trim();
      var own = [];
      var shared = [];
      slides.forEach(function (slide, i) {
        var sc = slide.getAttribute('data-colour') || '';
        if (!sc) shared.push(i);
        else if (sc === c) own.push(i);
      });
      order = own.length ? own.concat(shared) : slides.map(function (_s, i) { return i; });

      thumbs.forEach(function (thumb, n) {
        var p = order.indexOf(n);
        thumb.hidden = p < 0;
        thumb.style.order = p < 0 ? '' : String(p);
      });
      var single = order.length < 2;
      if (prev) prev.hidden = single;
      if (next) next.hidden = single;
      go(0);
    };

    // Let another block jump the gallery to a media id.
    root.velaGoToMedia = function (mediaId) {
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('data-media-id') === String(mediaId)) {
          var p = order.indexOf(i);
          if (p > -1) { go(p); return true; }
        }
      }
      return false;
    };

    var startColour = root.getAttribute('data-start-colour');
    if (startColour) root.velaSetColour(startColour, oos ? oos.hidden : true);
    else go(0);
  }

  /* ------------------------------------------------------------------- rail */

  function VelaRail(root) {
    if (!claim(root, 'rail')) return;

    var rail = root.querySelector('[data-vela-track]');
    var prev = root.querySelector('[data-vela-rail-prev]');
    var next = root.querySelector('[data-vela-rail-next]');
    if (!rail) return;

    // Step one cell at a time. Position is resolved to the nearest cell index
    // first, so the ends behave predictably even when the last cells are all
    // visible at once.
    function go(direction) {
      var cells = Array.prototype.slice.call(rail.children);
      if (!cells.length) return;

      var pad = parseFloat(getComputedStyle(rail).scrollPaddingLeft) || 0;
      var railLeft = rail.getBoundingClientRect().left;
      var offsets = cells.map(function (cell) {
        return Math.round(rail.scrollLeft + (cell.getBoundingClientRect().left - railLeft));
      });

      var current = rail.scrollLeft + pad;
      var index = 0;
      var closest = Infinity;

      for (var i = 0; i < offsets.length; i++) {
        var distance = Math.abs(offsets[i] - current);
        if (distance < closest) { closest = distance; index = i; }
      }

      var wanted = Math.max(0, Math.min(offsets.length - 1, index + direction));
      var target = wanted === index
        ? (direction > 0 ? rail.scrollWidth : 0)
        : offsets[wanted] - pad;

      scrollRailTo(rail, target, sync);
    }

    function sync() {
      if (!prev || !next) return;
      var max = rail.scrollWidth - rail.clientWidth - 2;
      prev.disabled = rail.scrollLeft <= 2;
      next.disabled = rail.scrollLeft >= max;
      prev.style.opacity = prev.disabled ? '0.35' : '';
      next.style.opacity = next.disabled ? '0.35' : '';
    }

    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });

    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  }

  /* ----------------------------------------------------------- sticky buy bar */

  function VelaSticky(root) {
    if (!claim(root, 'sticky')) return;

    var watchSelector = root.getAttribute('data-watch') || '[data-vela-bundle]';
    var target = document.querySelector(watchSelector);
    var button = root.querySelector('[data-vela-sticky-cta]');

    if (button) {
      button.addEventListener('click', function (event) {
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      });
    }

    if (!target || !('IntersectionObserver' in window)) return;

    // Show the bar only once the real buy box has scrolled out of view.
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var passed = entry.boundingClientRect.top < 0 && !entry.isIntersecting;
          root.classList.toggle('is-on', passed);
        });
      },
      { threshold: 0 }
    );

    io.observe(target);
  }

  /* ------------------------------------------------------------------- boot */

  var components = [
    ['[data-vela-bundle]', VelaBundle],
    ['[data-vela-reels]', VelaReels],
    ['[data-vela-gallery]', VelaGallery],
    ['[data-vela-rail]', VelaRail],
    ['[data-vela-sticky]', VelaSticky]
  ];

  function boot(root) {
    var scope = root || document;
    components.forEach(function (pair) {
      scope.querySelectorAll(pair[0]).forEach(function (node) { pair[1](node); });
      if (scope !== document && scope.matches && scope.matches(pair[0])) pair[1](scope);
    });
    initReveal(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // Re-run when the theme editor swaps a section in.
  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
  document.addEventListener('shopify:section:select', function (event) { boot(event.target); });

  window.VelaBoot = boot;
})();
